import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Search,
  Info,
  Plus,
  AlertTriangle,
  Eye,
  Megaphone,
} from 'lucide-react';
import type { NivelAtencao } from '../../api/hooks/useObservacoes';
import {
  CATEGORIA_CONFIG,
  NIVEL_CONFIG,
} from './observacoes-professor-shared';
import {
  descricaoExibicao,
  formatDataCurta,
  isAvisoPedagogico,
} from './observacoes-utils';
import { ObservacaoDetalhePanel } from './ObservacaoDetalhePanel';
import { ObservacoesFormFields } from './ObservacoesFormFields';
import { ObservacoesAlunoPicker } from './ObservacoesAlunoPicker';
import { Modal } from '../../components/ui/Modal';
import { btnPrimary, btnSecondary } from './observacoes-professor-shared';
import type { ObservacoesProfessorPageContext } from './useObservacoesProfessorPage';
import './observacoes-professor-desktop.css';
import './professor-dashboard-desktop.css';

interface Props {
  ctx: ObservacoesProfessorPageContext;
}

export function ObservacoesProfessorDesktop({ ctx }: Props) {
  const navigate = useNavigate();
  const {
    filtroCategoria,
    setFiltroCategoria,
    filtroNivel,
    setFiltroNivel,
    filtroTurma,
    setFiltroTurma,
    showForm,
    setShowForm,
    editingId,
    selectedId,
    setSelectedId,
    observacaoSelecionada,
    observacoes,
    isLoading,
    minhasTurmas,
    stats,
    openNovaObservacao,
    openEdit,
    handleRemover,
    handleSalvar,
    resetForm,
    form,
    criar,
    atualizar,
  } = ctx;

  const [busca, setBusca] = useState('');

  const filtered = useMemo(() => {
    let list = observacoes;
    const q = busca.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (o) =>
          o.titulo.toLowerCase().includes(q) ||
          (o.aluno?.nome || '').toLowerCase().includes(q) ||
          descricaoExibicao(o.descricao || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => {
      const ua = a.nivelAtencao === 'URGENTE';
      const ub = b.nivelAtencao === 'URGENTE';
      if (ua && !ub) return -1;
      if (!ua && ub) return 1;
      return (b.criadoEm || '').localeCompare(a.criadoEm || '');
    });
  }, [observacoes, busca]);

  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filtered.some((o) => o.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId, setSelectedId]);

  const setKpiNivel = (nivel: NivelAtencao | '') => {
    setFiltroNivel(nivel);
  };

  return (
    <>
      <div className="admin-page text-[13px] leading-snug dash-desk-page obs-desk-page">
        <div className="dash-desk-root obs-desk-root">
          <div className="dash-desk-top obs-desk-top">
            <div className="dash-desk-greeting">
              <h1 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                Observacoes pedagogicas
              </h1>
              <p className="obs-desk-subtitle">
                Registros internos · avisos a familia em Meus Alunos
              </p>
            </div>
            <BookOpen size={18} style={{ color: '#059669', opacity: 0.85 }} aria-hidden />
          </div>

          <div className="obs-desk-metrics">
            <button
              type="button"
              className={`obs-desk-kpi-btn${!filtroNivel && !filtroCategoria ? ' obs-desk-kpi-btn--active' : ''}`}
              onClick={() => {
                setFiltroNivel('');
                setFiltroCategoria('');
              }}
            >
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
                  <BookOpen size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{stats.total}</div>
                  <div className="dash-desk-kpi-lbl">Total</div>
                </div>
              </div>
            </button>
            <button
              type="button"
              className={`obs-desk-kpi-btn${filtroNivel === 'URGENTE' ? ' obs-desk-kpi-btn--active' : ''}`}
              onClick={() => setKpiNivel(filtroNivel === 'URGENTE' ? '' : 'URGENTE')}
            >
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                  <AlertTriangle size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{stats.urgentes}</div>
                  <div className="dash-desk-kpi-lbl">Urgentes</div>
                </div>
              </div>
            </button>
            <button
              type="button"
              className={`obs-desk-kpi-btn${filtroNivel === 'ATENCAO' ? ' obs-desk-kpi-btn--active' : ''}`}
              onClick={() => setKpiNivel(filtroNivel === 'ATENCAO' ? '' : 'ATENCAO')}
            >
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
                  <Eye size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{stats.atencao}</div>
                  <div className="dash-desk-kpi-lbl">Atencao</div>
                </div>
              </div>
            </button>
            <div className="dash-desk-kpi">
              <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                <Megaphone size={15} />
              </div>
              <div>
                <div className="dash-desk-kpi-val">{stats.avisos}</div>
                <div className="dash-desk-kpi-lbl">Avisos fam.</div>
              </div>
            </div>
          </div>

          <div className="obs-desk-notice">
            <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              <strong>Registro interno</strong> (pode ser privado). Para avisar a familia:{' '}
              <button type="button" onClick={() => navigate('/professor/alunos')}>
                Meus Alunos → Avisar responsavel
              </button>
              . Alunos via grade (
              <code style={{ fontSize: '0.65rem' }}>chamadas/turma/…/alunos</code>).
            </span>
          </div>

          <div className="obs-desk-toolbar">
            <div className="obs-desk-search-wrap">
              <Search size={14} />
              <input
                type="search"
                className="obs-desk-search"
                placeholder="Buscar titulo, aluno, texto…"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
            <select
              className="obs-desk-filter"
              value={filtroTurma}
              onChange={(e) => setFiltroTurma(e.target.value)}
              aria-label="Turma"
            >
              <option value="">Todas turmas</option>
              {minhasTurmas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
            <select
              className="obs-desk-filter"
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value as typeof filtroCategoria)}
              aria-label="Categoria"
            >
              <option value="">Todas categorias</option>
              {Object.entries(CATEGORIA_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
            <button type="button" className="obs-desk-btn-nova" onClick={openNovaObservacao}>
              <Plus size={14} />
              Nova
            </button>
            <span className="obs-desk-count">{filtered.length} registro(s)</span>
          </div>

          <div className="obs-desk-body">
            <div className="obs-desk-list-card">
              <div className="obs-desk-list-scroll">
                {isLoading ? (
                  <p className="obs-desk-loading">Carregando observacoes…</p>
                ) : filtered.length === 0 ? (
                  <p className="obs-desk-loading">Nenhuma observacao encontrada.</p>
                ) : (
                  filtered.map((obs) => {
                    const catInfo = CATEGORIA_CONFIG[obs.categoria];
                    const nivelInfo = NIVEL_CONFIG[obs.nivelAtencao];
                    const aviso = isAvisoPedagogico(obs);
                    const active = selectedId === obs.id;
                    const desc = descricaoExibicao(obs.descricao || '');
                    return (
                      <button
                        key={obs.id}
                        type="button"
                        className={[
                          'obs-desk-list-item',
                          active ? 'obs-desk-list-item--active' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{ borderLeftColor: nivelInfo.color }}
                        onClick={() => setSelectedId(obs.id)}
                      >
                        <div
                          className="obs-desk-list-icon"
                          style={{ background: catInfo.bg, color: catInfo.color }}
                        >
                          {aviso ? <Megaphone size={14} /> : catInfo.icon}
                        </div>
                        <div className="obs-desk-list-main">
                          <p className="obs-desk-list-title">{obs.titulo}</p>
                          <p className="obs-desk-list-meta">
                            {obs.aluno?.nome || '—'} · {formatDataCurta(obs.criadoEm)}
                            {desc ? ` · ${desc}` : ''}
                          </p>
                        </div>
                        <div className="obs-desk-list-aside">
                          <span
                            className="obs-desk-list-nivel"
                            style={{ background: nivelInfo.bg, color: nivelInfo.color }}
                          >
                            {nivelInfo.label}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="obs-desk-detail-card">
              <div className="obs-desk-detail-scroll">
                {observacaoSelecionada ? (
                  <ObservacaoDetalhePanel
                    obs={observacaoSelecionada}
                    onEdit={openEdit}
                    onRemove={handleRemover}
                  />
                ) : (
                  <div className="obs-desk-detail-empty">
                    Selecione um registro para ver detalhes, editar ou remover (ate 7 dias).
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showForm}
        onClose={resetForm}
        title={editingId ? 'Editar observacao' : 'Nova observacao'}
        size="lg"
        contentClassName="obs-desk-nova-modal"
        footer={
          <div className="obs-desk-modal-footer">
            <button type="button" style={btnSecondary} onClick={resetForm}>
              Cancelar
            </button>
            <button
              type="button"
              style={btnPrimary}
              onClick={handleSalvar}
              disabled={
                !form.titulo ||
                !form.descricao ||
                !form.alunoId ||
                criar.isPending ||
                atualizar?.isPending
              }
            >
              {criar.isPending || atualizar?.isPending
                ? 'Salvando...'
                : editingId
                  ? 'Atualizar'
                  : 'Salvar observacao'}
            </button>
          </div>
        }
      >
        <ObservacoesFormFields {...ctx} hideActions />
      </Modal>

      <ObservacoesAlunoPicker {...ctx} variant="modal" />
    </>
  );
}
